import { Typography } from "@/components/atoms";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React, { useEffect, useRef } from "react";
import { Animated, Easing, Platform, Pressable, View } from "react-native";

export interface GuestLanguageToggleProps {
  value: "en" | "ar";
  onChange: (value: "en" | "ar") => void;
}

export const GUEST_LANGUAGE_TOGGLE_WIDTH = 76;
const CONTROL_WIDTH = GUEST_LANGUAGE_TOGGLE_WIDTH;
const PADDING = 3;
const INNER_WIDTH = CONTROL_WIDTH - PADDING * 2;
const SEGMENT_WIDTH = INNER_WIDTH / 2;

/**
 * Compact "EN | ع" pill for guest screens — the shared LanguageSwitch
 * (src/modules/profile/components/LanguageSwitch.tsx) spells out
 * "English"/"العربية" and is used by Profile + web's guest chrome, so
 * shortening its labels here would change those too. This is a standalone,
 * guest-mobile-only copy of the same deterministic-slot mirroring logic,
 * just with abbreviated labels and a smaller footprint.
 */
export function GuestLanguageToggle({ value, onChange }: Readonly<GuestLanguageToggleProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const mirrorOrder = Platform.OS !== "web" && isRTL;
  const firstLang: "en" | "ar" = mirrorOrder ? "ar" : "en";
  const secondLang: "en" | "ar" = mirrorOrder ? "en" : "ar";

  const progress = useRef(new Animated.Value(value === firstLang ? 0 : 1)).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);

  useEffect(() => {
    const targetValue = value === firstLang ? 0 : 1;
    if (animationRef.current) {
      animationRef.current.stop();
    }
    animationRef.current = Animated.timing(progress, {
      toValue: targetValue,
      duration: 150,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    animationRef.current.start(() => {
      animationRef.current = null;
    });
    return () => {
      if (animationRef.current) {
        animationRef.current.stop();
        animationRef.current = null;
      }
    };
  }, [value, progress, firstLang]);

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, SEGMENT_WIDTH],
  });

  const renderLabel = (lang: "en" | "ar") => (
    <Typography
      variant="caption"
      style={{
        color: value === lang ? colors.white : colors.fontPrimary,
        fontFamily: "Matter-SemiBold",
        ...(lang === "ar" && Platform.OS !== "web"
          ? { fontFamily: Platform.OS === "ios" ? "System" : "sans-serif" }
          : null),
      }}
    >
      {lang === "en" ? "EN" : "ع"}
    </Typography>
  );

  return (
    <View
      style={{
        position: "relative",
        direction: "ltr",
        flexDirection: "row",
        alignItems: "center",
        padding: PADDING,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.outlineActive,
        overflow: "hidden",
        backgroundColor: colors.backgroundLevel1,
        width: CONTROL_WIDTH,
        minWidth: CONTROL_WIDTH,
      }}
    >
      <Animated.View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: PADDING,
          top: PADDING,
          bottom: PADDING,
          width: SEGMENT_WIDTH,
          borderRadius: 13,
          backgroundColor: colors.fontPrimary,
          transform: [{ translateX }],
        }}
      />
      <Pressable
        accessibilityRole="button"
        onPress={() => onChange(firstLang)}
        style={{
          width: SEGMENT_WIDTH,
          paddingVertical: 6,
          borderRadius: 13,
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        {renderLabel(firstLang)}
      </Pressable>
      <Pressable
        accessibilityRole="button"
        onPress={() => onChange(secondLang)}
        style={{
          width: SEGMENT_WIDTH,
          paddingVertical: 6,
          borderRadius: 13,
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        {renderLabel(secondLang)}
      </Pressable>
    </View>
  );
}

export default GuestLanguageToggle;
